import type { InvoicingRunStatusEnum } from "@sidegigllc/api/client";

/** The statuses ops can filter the run queue by, and the one place they are
 * listed. The route's `validateSearch` enum and the Status column's select are
 * both built from this.
 *
 * They have to agree: `hardenSearchValidation` coerces a param the schema does
 * not recognise back out of the URL, so a value offered by the select but
 * missing from the schema selects and then silently does nothing — no error,
 * no request, no clue.
 *
 * Deliberately a subset of `InvoicingRunStatusEnum`, so `satisfies` still
 * catches a typo while leaving the omissions intentional: `CREATING` is a
 * transient state a run passes through in seconds and `SENDING` is never
 * emitted at all, so offering either would be a filter that reliably matches
 * nothing. See STATUS_LABELS in `columns.tsx` for the labels themselves. */
export const RUN_STATUS_FILTERS = [
  "DRAFT",
  "CREATED",
  "SENT",
  "DISCARDED",
  "DELETED",
  "BACKFILL",
] as const satisfies ReadonlyArray<InvoicingRunStatusEnum>;

export type RunStatusFilter = (typeof RUN_STATUS_FILTERS)[number];

/** Ops-facing wording for ``InvoicingRunStatus``. Typed
 * ``Record<InvoicingRunStatusEnum, string>`` so a status added later fails the
 * typecheck instead of rendering a blank chip. Covers every status, while
 * RUN_STATUS_FILTERS above deliberately offers only some — the two lists are
 * not meant to match.
 *
 * SENT used to be in the unfilterable group: WORK-8251 shipped the manual send
 * endpoint, so runs now really do reach SENT and `?status=SENT` is a legal
 * filter value the backend answers. CREATING and SENDING stay out — CREATING is
 * a transient in-flight marker a run passes through in seconds, and SENDING is
 * still never emitted — so filtering by either would reliably match nothing.
 * DELETED is filterable for the same reason SENT is: WORK-8416's
 * invoiced-delete endpoint moves CREATED / SENT runs there, and the list
 * endpoint accepts `?status=DELETED`. BACKFILL (CORE-8911) is filterable too:
 * the switch-on backfill writes those runs straight into that status, and the
 * list endpoint accepts `?status=BACKFILL`.
 * Do not "fix" the remaining asymmetry into matching; check whether the status
 * is actually emitted first.
 *
 * Lives here, beside the filter tuple, because both views that show a run's
 * status read it: the queue's Status column and the detail page's
 * `RunStatusChip`. They rendered the same value two different ways once the
 * column started labelling and the badge did not. */
export const STATUS_LABELS: Record<InvoicingRunStatusEnum, string> = {
  DRAFT: "Draft",
  CREATING: "Creating",
  CREATED: "Created",
  SENDING: "Sending",
  SENT: "Sent",
  DISCARDED: "Discarded",
  DELETED: "Deleted",
  BACKFILL: "Backfill",
};
